import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Compass3D } from '../decor/compass/Compass3D'
import { CompassLines } from '../decor/compass/VerticalShellCompassLines'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoAssetModel,
  GeckoMeasurement,
  GeckoVerticalTankFoundation,
} from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a tank foundation, plotted on a cartesian plane, with the
 * center of its base at "position" and decorated with Rich Asset features
 * specific to the Foundation.
 * @param model               The Vertical Tank Foundation component model
 * @param assetModel          The asset model for the asset that the Foundation is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */

export const FoundationSurface3D: React.FC<
  {
    model: GeckoVerticalTankFoundation
    assetModel: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, leakDetectionValves } = React.useMemo(() => {
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num,
            model.componentBounds.height.num,
            model.radius.num
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )
    return {
      bounds: model.componentBounds,
      scale: scale,
      leakDetectionValves: model.leakDetectionValvesArray,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry
  }, [show3D])

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      const fullCircle = 2 * Math.PI
      if (show3D) return [-Math.cos(radians), 1.0, Math.sin(radians)]
      const positionAlongWidth = model.componentBounds.width.scale(
        radians / fullCircle
      )
      const relativePositionAlongWidth = model.componentBounds.start.x
        .plus(positionAlongWidth)
        .scale(1.0 / model.componentBounds.width.num).num
      return [relativePositionAlongWidth, 1.0, 0]
    },
    [show3D, model]
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num

      if (!show3D) return [relX, 0, 0]
      const angle = 2.0 * Math.PI * relX
      return [-Math.cos(angle), 0, Math.sin(angle)]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(bounds.y.unit)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      return [-0.875, relY, 0.5]
    },
    [show3D, bounds]
  )

  return (
    <Surface3D
      geometry={geometry}
      bounds={bounds}
      wrapMode={
        show3D
          ? ComponentSurfaceWrappingMode.ComponentWrapsX
          : ComponentSurfaceWrappingMode.UnrolledComponentWrapsX
      }
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <TankDecorGroup3D yPivot={show3D}>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeEnd
          includeStart
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
      <TankDecorGroup3D xPivot={false}>
        <Ruler3D
          anchor={'top'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd={!show3D}
          getTickPosition={positionXTick}
        />
      </TankDecorGroup3D>

      <Compass3D
        componentModel={model}
        angleDeltaDegrees={30}
        assetModel={assetModel}
        getRotationPosition={getCompassPosition}
      />

      <CompassLines
        angleDeltaDegrees={30}
        assetModel={assetModel}
        getRotationPosition={getCompassPosition}
      />
      <FeatureDispatch features={leakDetectionValves} />
    </Surface3D>
  )
}
